import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BedDouble, Trash2, Plus, Building2, Home, Hotel, House, MapPin, Users, Filter, Upload, Camera, X as XIcon, Mail, MessageCircle } from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { sortByUpcoming, EmailLink, PhoneLink } from "../lib/formatters";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";

const BED_NOS = Array.from({ length: 24 }, (_, i) => String(i + 1).padStart(2, "0"));

const bedsInRoom = (room) => {
  const spec = (room?.extra || "").trim();
  if (!spec) return BED_NOS;
  const out = new Set();
  for (const part of spec.split(",").map(s => s.trim()).filter(Boolean)) {
    const m = part.match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) {
      const a = parseInt(m[1]), b = parseInt(m[2]);
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) out.add(String(i).padStart(2, "0"));
    } else if (/^\d+$/.test(part)) {
      out.add(part.padStart(2, "0"));
    }
  }
  return out.size ? Array.from(out) : BED_NOS;
};

const PROPERTY_ICON = {
  "Company Accommodation": Building2,
  "Host Family": Home,
  "Hotel": Hotel,
  "Guest House": House,
};

export default function Rooms() {
  const { user } = useAuth();
  const canEdit = ["FOUNDER", "BDOM", "GEHM", "PCWM"].includes(user.real_role);
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [accs, setAccs] = useState([]);
  const [propTypes, setPropTypes] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false);
  const [addForm, setAddForm] = useState({ open: false, country: "", city: "", property_type: "", name: "", address: "", notes: "" });
  const [addRoom, setAddRoom] = useState({ open: false, accommodation_id: "", room_number: "", extra: "" });
  const [editingAcc, setEditingAcc] = useState(null);
  const [occByAcc, setOccByAcc] = useState({}); // { accId: {total_beds, series} }
  const csvRef = useRef();
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const [b, r, a, pt] = await Promise.all([
      api.get("/program-bookings"),
      api.get("/rooms"),
      api.get("/room-allocations"),
      api.get("/accommodations/property-types"),
    ]);
    const acs = await api.get("/accommodations");
    setBookings(sortByUpcoming(b.data.map(x => ({ ...x, start: x.start_date, end: x.end_date })), "start"));
    setRooms(r.data);
    setAccs(acs.data);
    setPropTypes(pt.data);
    const map = {};
    for (const x of a.data) map[x.booking_id] = x;
    setAllocs(map);
  };
  useEffect(() => { load(); }, []);

  // Lazily fetch a next-30-day occupancy series for every accommodation.
  // Only runs once accommodations arrive; results memoised in state so the
  // card sparkline paints instantly on re-render.
  useEffect(() => {
    if (!accs.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(accs.map(async a => {
        try {
          const { data } = await api.get(`/accommodations/${a.id}/occupancy?days=30`);
          return [a.id, data];
        } catch { return [a.id, { total_beds: 0, series: [] }]; }
      }));
      if (!cancelled) setOccByAcc(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [accs]);

  const onCsvChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = await api.post("/accommodations/bulk-import", { csv: text });
      const parts = [];
      if (data.properties_created) parts.push(`${data.properties_created} propert${data.properties_created !== 1 ? "ies" : "y"} created`);
      if (data.properties_reused) parts.push(`${data.properties_reused} reused`);
      if (data.rooms_created) parts.push(`${data.rooms_created} room${data.rooms_created !== 1 ? "s" : ""} added`);
      if (data.rooms_skipped) parts.push(`${data.rooms_skipped} duplicate room${data.rooms_skipped !== 1 ? "s" : ""} skipped`);
      toast.success(parts.length ? parts.join(" · ") : "Nothing to import");
      if (data.errors && data.errors.length) {
        toast.error(`${data.errors.length} row${data.errors.length !== 1 ? "s" : ""} had errors — first: ${data.errors[0]}`, { duration: 6000 });
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  // --- Accommodation form -----------------------------------------
  const saveAcc = async () => {
    const { country, city, property_type, name, address, notes } = addForm;
    if (!country.trim() || !city.trim() || !property_type || !name.trim()) {
      toast.error("Country, City, Type and Name are required"); return;
    }
    try {
      await api.post("/accommodations", { country: country.trim(), city: city.trim(), property_type, name: name.trim(), address, notes });
      toast.success(`Added ${name} in ${city}, ${country}`);
      setAddForm({ open: false, country: "", city: "", property_type: "", name: "", address: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delAcc = async (aid, label) => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try { await api.delete(`/accommodations/${aid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // --- Room form (a numbered room inside a property) --------------
  const saveRoom = async () => {
    const { accommodation_id, room_number, extra } = addRoom;
    if (!accommodation_id || !room_number.trim()) {
      toast.error("Choose a property and enter a room number"); return;
    }
    const acc = accs.find(a => a.id === accommodation_id);
    if (!acc) return;
    const name = `${acc.name} · ${room_number.trim()}`;
    // Duplicate guard
    if (rooms.some(r => r.name === name)) {
      toast.error(`Room ${room_number} already exists in ${acc.name}`); return;
    }
    try {
      await api.post("/rooms", { name, extra: extra || "", accommodation_id, room_number: room_number.trim() });
      toast.success(`Added ${name}`);
      setAddRoom({ open: false, accommodation_id: "", room_number: "", extra: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delRoom = async (rid, name) => {
    setDelRoomTarget({ id: rid, name });
  };
  const [delRoomTarget, setDelRoomTarget] = useState(null);
  const doDelRoom = async () => {
    if (!delRoomTarget) return;
    try { await api.delete(`/rooms/${delRoomTarget.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setDelRoomTarget(null);
  };

  // ---- Photo gallery per accommodation ---------------------------
  const photoInputs = useRef({});
  const uploadPhoto = async (acc, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo too large (max 8 MB)"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: fileRes } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const nextPhotos = [ ...(acc.photos || []), fileRes.id ];
      await api.patch(`/accommodations/${acc.id}`, {
        country: acc.country, city: acc.city, property_type: acc.property_type,
        name: acc.name, address: acc.address || "", notes: acc.notes || "",
        photos: nextPhotos,
      });
      toast.success(`Added photo to ${acc.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { if (photoInputs.current[acc.id]) photoInputs.current[acc.id].value = ""; }
  };
  const removePhoto = async (acc, fileId) => {
    const nextPhotos = (acc.photos || []).filter(id => id !== fileId);
    try {
      await api.patch(`/accommodations/${acc.id}`, {
        country: acc.country, city: acc.city, property_type: acc.property_type,
        name: acc.name, address: acc.address || "", notes: acc.notes || "",
        photos: nextPhotos,
      });
      toast.success("Photo removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // --- Allocations ------------------------------------------------
  const save = async (b, patch) => {
    const cur = allocs[b.id] || { booking_id: b.id, customer: b.customer, room: "", bed_no: "" };
    const doc = { ...cur, ...patch };
    if (!doc.room || !doc.bed_no) {
      setAllocs(m => ({ ...m, [b.id]: doc }));
      return;
    }
    await api.post("/room-allocations", { booking_id: b.id, customer: b.customer, room: doc.room, bed_no: doc.bed_no });
    setAllocs(m => ({ ...m, [b.id]: doc }));
    toast.success(`Allocated ${b.customer} → ${doc.room} bed ${doc.bed_no}`);
  };

  // Precompute {room_name -> array of {bookingId, bed}} once — used to derive
  // the "occupied beds" set per booking without an O(n²) sweep in render.
  const bedsByRoom = useMemo(() => {
    const m = {};
    for (const x of Object.values(allocs)) {
      if (!x.room) continue;
      (m[x.room] = m[x.room] || []).push({ booking_id: x.booking_id, bed_no: x.bed_no });
    }
    return m;
  }, [allocs]);

  const roomByName = useMemo(() => {
    const m = {};
    for (const r of rooms) m[r.name] = r;
    return m;
  }, [rooms]);

  // Derived groupings ---------------------------------------------
  const roomsByAcc = useMemo(() => {
    const m = {};
    for (const r of rooms) {
      const k = r.accommodation_id || "_legacy";
      (m[k] = m[k] || []).push(r);
    }
    return m;
  }, [rooms]);

  const accByRoomName = useMemo(() => {
    const m = {};
    for (const r of rooms) {
      const acc = accs.find(a => a.id === r.accommodation_id);
      if (acc) m[r.name] = acc;
    }
    return m;
  }, [rooms, accs]);

  const accsByCountryCity = useMemo(() => {
    const m = {};
    for (const a of accs) {
      const key = `${a.country} · ${a.city}`;
      (m[key] = m[key] || []).push(a);
    }
    return m;
  }, [accs]);

  // Occupancy by destination → house → room → pax (nested grouping so a
  // busy office sees "5 people spread across Jaipur House rooms 12/17/18"
  // instead of a flat list of 5 individual cards.)
  const occupancyByDest = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      const a = allocs[b.id];
      if (!a?.room) continue;
      const acc = accByRoomName[a.room];
      if (!acc) continue;
      const destKey = `${acc.country} · ${acc.city}`;
      m[destKey] = m[destKey] || { country: acc.country, city: acc.city, occupants: 0, houses: {} };
      m[destKey].occupants += 1;
      // House level — one entry per accommodation.
      const houseKey = acc.id || acc.name;
      m[destKey].houses[houseKey] = m[destKey].houses[houseKey] || {
        accommodation: acc, count: 0, rooms: {},
      };
      m[destKey].houses[houseKey].count += 1;
      // Room level — one bucket per room number.
      const roomKey = a.room;
      m[destKey].houses[houseKey].rooms[roomKey] = m[destKey].houses[houseKey].rooms[roomKey] || {
        room: a.room, pax: [],
      };
      m[destKey].houses[houseKey].rooms[roomKey].pax.push({ booking: b, bed_no: a.bed_no });
    }
    return m;
  }, [bookings, allocs, accByRoomName]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2"><BedDouble className="text-brand"/><h1 className="text-2xl font-semibold">Accommodation</h1></div>

      {/* ---------- Add Accommodation ---------- */}
      {canEdit && (
        <div className="aii-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Building2 size={16} className="text-brand"/> Accommodations</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Country → City → Type → Name → Address. Participants will see this address in their portal.</p>
            </div>
            <button data-testid="acc-add-btn" onClick={() => setAddForm(f => ({ ...f, open: !f.open }))} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
              <Plus size={14}/> {addForm.open ? "Close" : "Add accommodation"}
            </button>
          </div>
          {/* CSV bulk import — one file seeds every property + room */}
          <div className="mb-3 flex flex-wrap items-center gap-2 p-2.5 border border-dashed border-[color:var(--aii-border)] rounded-md bg-[color:var(--aii-bg)]/50">
            <div className="text-xs text-[color:var(--aii-text-2)] flex-1 min-w-0">
              <div className="font-semibold text-[color:var(--aii-text-1)]">Bulk import (CSV)</div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">Columns: <code>property_name, country, city, property_type, address, room_number, bed_range</code>. Rows sharing a property collapse into one accommodation; rooms attach automatically.</div>
            </div>
            <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={onCsvChange} className="hidden" data-testid="acc-csv-input"/>
            <button onClick={() => csvRef.current?.click()} disabled={importing} data-testid="acc-csv-btn"
              className="text-xs px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5 disabled:opacity-50">
              <Upload size={12}/> {importing ? "Importing…" : "Upload CSV"}
            </button>
          </div>
          {addForm.open && (
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-4 p-3 bg-[color:var(--aii-bg)] rounded-md" data-testid="acc-form">
              <input className="input-aii sm:col-span-1" placeholder="Country" value={addForm.country} onChange={e => setAddForm(f => ({ ...f, country: e.target.value }))} data-testid="acc-country"/>
              <input className="input-aii sm:col-span-1" placeholder="City" value={addForm.city} onChange={e => setAddForm(f => ({ ...f, city: e.target.value }))} data-testid="acc-city"/>
              <select className="input-aii sm:col-span-1" value={addForm.property_type} onChange={e => setAddForm(f => ({ ...f, property_type: e.target.value }))} data-testid="acc-type">
                <option value="">— Type —</option>
                {propTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="input-aii sm:col-span-1" placeholder="Property name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} data-testid="acc-name"/>
              <input className="input-aii sm:col-span-2" placeholder="Full address" value={addForm.address} onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} data-testid="acc-address"/>
              <button onClick={saveAcc} className="sm:col-span-6 py-2 bg-brand text-white rounded font-semibold hover:bg-brand-dark" data-testid="acc-save">Save accommodation</button>
            </div>
          )}
          {/* Grouped list */}
          <div className="space-y-3">
            {Object.keys(accsByCountryCity).length === 0 && (
              <div className="text-sm text-[color:var(--aii-muted)] py-4 text-center italic">No accommodations yet — add your first above.</div>
            )}
            {Object.entries(accsByCountryCity).sort().map(([label, items]) => (
              <div key={label} className="border border-[color:var(--aii-border)] rounded-md p-3">
                <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1 mb-2"><MapPin size={11}/> {label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {items.map(a => {
                    const Icon = PROPERTY_ICON[a.property_type] || Building2;
                    const roomCount = (roomsByAcc[a.id] || []).length;
                    return (
                      <div key={a.id} className="border border-[color:var(--aii-border)] rounded p-3 hover:border-brand hover:shadow-sm transition" data-testid={`acc-item-${a.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Icon size={14} className="text-brand flex-shrink-0"/>
                              <div className="font-semibold text-sm truncate">{a.name}</div>
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">{a.property_type}</div>
                            {a.address && <div className="text-[11px] text-[color:var(--aii-text-2)] mt-1 leading-snug">{a.address}</div>}
                            <div className="text-[10px] mt-1.5 flex items-center gap-2">
                              <span className="pill pill-green">{roomCount} room{roomCount !== 1 && "s"}</span>
                              {canEdit && (
                                <button type="button" onClick={() => setEditingAcc(a)}
                                        data-testid={`acc-edit-${a.id}`}
                                        className="text-[10px] text-brand font-semibold hover:underline">Edit</button>
                              )}
                            </div>
                            <OccupancySparkline data={occByAcc[a.id]} testId={`acc-spark-${a.id}`}/>
                            <PhotoGallery
                              acc={a}
                              canEdit={canEdit}
                              onUpload={(f) => uploadPhoto(a, f)}
                              onRemove={(fid) => removePhoto(a, fid)}
                              inputRef={(el) => { photoInputs.current[a.id] = el; }}
                            />
                          </div>
                          {canEdit && (
                            <button onClick={() => delAcc(a.id, a.name)} className="text-[color:var(--aii-muted)] hover:text-red-600 flex-shrink-0" title="Remove" data-testid={`acc-del-${a.id}`}>
                              <Trash2 size={12}/>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Add Room to Accommodation ---------- */}
      {canEdit && (
        <div className="aii-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><BedDouble size={16} className="text-brand"/> Rooms</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Select a property, add a room number. Beds default to 24; override via range e.g. <b>1-8</b>.</p>
            </div>
            <button onClick={() => setAddRoom(r => ({ ...r, open: !r.open }))} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5" data-testid="room-add-btn">
              <Plus size={14}/> {addRoom.open ? "Close" : "Add room"}
            </button>
          </div>
          {addRoom.open && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 bg-[color:var(--aii-bg)] rounded-md" data-testid="room-form">
              <select className="input-aii sm:col-span-2" value={addRoom.accommodation_id} onChange={e => setAddRoom(r => ({ ...r, accommodation_id: e.target.value }))} data-testid="room-acc">
                <option value="">— Property —</option>
                {accs.map(a => <option key={a.id} value={a.id}>{a.country} · {a.city} · {a.name} ({a.property_type})</option>)}
              </select>
              <input className="input-aii" placeholder="Room number (e.g. 101)" value={addRoom.room_number} onChange={e => setAddRoom(r => ({ ...r, room_number: e.target.value }))} data-testid="room-num"/>
              <input className="input-aii" placeholder="Bed range (opt, e.g. 1-4)" value={addRoom.extra} onChange={e => setAddRoom(r => ({ ...r, extra: e.target.value }))} data-testid="room-extra"/>
              <button onClick={saveRoom} className="sm:col-span-4 py-2 bg-brand text-white rounded font-semibold hover:bg-brand-dark" data-testid="room-save">Save room</button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Bookings — allocate room & bed ---------- */}
      <div className="aii-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold">Bookings — allocate room &amp; bed</h3>
          <button
            data-testid="rooms-unassigned-toggle"
            onClick={() => setShowOnlyUnassigned(s => !s)}
            className={`text-xs px-3 py-1.5 rounded-md border font-semibold flex items-center gap-1.5 transition ${
              showOnlyUnassigned
                ? "bg-brand text-white border-brand"
                : "bg-white text-[color:var(--aii-text-1)] border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"
            }`}
          >
            <Filter size={11}/> {showOnlyUnassigned ? "Showing unassigned only" : "Show only unassigned beds"}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${showOnlyUnassigned ? "bg-white/20" : "bg-brand/10 text-brand"}`} data-testid="rooms-unassigned-count">
              {bookings.filter(b => !((allocs[b.id]||{}).room && (allocs[b.id]||{}).bed_no)).length}
            </span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr><th>Partner</th><th>Participant</th><th>Gender</th><th>DOB</th><th>Programme</th><th>Destination</th><th>City</th><th>Start</th><th>End</th><th>Room</th><th>Bed no.</th></tr></thead>
            <tbody>
              {bookings.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-[color:var(--aii-muted)]">No bookings.</td></tr>}
              {bookings.filter(b => !showOnlyUnassigned || !((allocs[b.id]||{}).room && (allocs[b.id]||{}).bed_no)).map(b => {
                const a = allocs[b.id] || {};
                const room = a.room ? roomByName[a.room] : null;
                const roomBeds = room ? bedsInRoom(room) : BED_NOS;
                const occupied = new Set(
                  (bedsByRoom[a.room] || [])
                    .filter(x => x.booking_id !== b.id)
                    .map(x => x.bed_no)
                );
                const availableBeds = roomBeds.filter(n => !occupied.has(n));
                const acc = a.room ? accByRoomName[a.room] : null;
                return (
                  <tr key={b.id}>
                    <td className="font-medium text-brand text-xs">{b.partner_name || <span className="italic text-[color:var(--aii-muted)] font-normal">—</span>}</td>
                    <td className="font-medium">
                      <div className="flex flex-col gap-0.5">
                        <span>{b.customer}</span>
                        {/* Universal contact strip — visible to every
                            role scoped to this destination so GEHM /
                            housekeeping can WhatsApp / email a guest
                            straight from the room allocation view. */}
                        {((b.email && !b.email.toLowerCase().endsWith("@participants.aiigroup.com")) || b.telephone || b.whatsapp || b.phone) && (
                          <div className="flex items-center gap-2 flex-wrap text-[11px] font-normal">
                            {b.email && !b.email.toLowerCase().endsWith("@participants.aiigroup.com") && (
                              <span data-testid={`alloc-email-${b.id}`} className="inline-flex items-center gap-1">
                                <Mail size={10} className="text-[color:var(--aii-muted)]"/>
                                <EmailLink value={b.email}/>
                              </span>
                            )}
                            {(b.whatsapp || b.telephone || b.phone) && (
                              <span data-testid={`alloc-phone-${b.id}`} className="inline-flex items-center gap-1">
                                <MessageCircle size={10} className="text-[#25D366]"/>
                                <PhoneLink value={b.whatsapp || b.telephone || b.phone}/>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-xs">{b.gender || "—"}</td>
                    <td className="text-xs whitespace-nowrap">{b.dob || "—"}</td>
                    <td>{b.programme || "—"}</td>
                    <td className="text-xs whitespace-nowrap">{b.destination || (acc && acc.country) || "—"}</td>
                    <td className="text-xs whitespace-nowrap">{b.city || (acc && acc.city) || "—"}</td>
                    <td className="whitespace-nowrap">{b.start || "—"}</td>
                    <td className="whitespace-nowrap">{b.end || "—"}</td>
                    <td>
                      <select value={a.room || ""} onChange={e => save(b, { room: e.target.value, bed_no: "" })} className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm min-w-[180px]" data-testid={`alloc-room-${b.id}`}>
                        <option value="">— select —</option>
                        {rooms.map(r => {
                          const macc = accs.find(x => x.id === r.accommodation_id);
                          const base = macc ? `${macc.city} · ${r.name}` : r.name;
                          const cap = bedsInRoom(r).length;
                          // How many of this room's beds are already taken by OTHER bookings.
                          const taken = (bedsByRoom[r.name] || []).filter(x => x.booking_id !== b.id).length;
                          return <option key={r.id} value={r.name}>{base} · {taken}/{cap} taken</option>;
                        })}
                      </select>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <select value={a.bed_no || ""} onChange={e => save(b, { bed_no: e.target.value })} disabled={!a.room}
                          className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm disabled:bg-[color:var(--aii-bg)]" data-testid={`alloc-bed-${b.id}`}>
                          <option value="">— select —</option>{availableBeds.map(n => <option key={n}>{n}</option>)}
                        </select>
                        {a.room && room && (() => {
                          const cap = bedsInRoom(room).length;
                          const takenOthers = (bedsByRoom[a.room] || []).filter(x => x.booking_id !== b.id).length;
                          const wouldFill = takenOthers + (a.bed_no ? 1 : 0);
                          const full = takenOthers >= cap;
                          const atCap = wouldFill === cap && !full;
                          const cls = full
                            ? "bg-red-50 text-red-700 border-red-200"
                            : atCap
                              ? "bg-amber-50 text-amber-800 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200";
                          return (
                            <span
                              data-testid={`alloc-cap-${b.id}`}
                              title={full ? "Room is full — no available beds left" : atCap ? "Room at capacity after this bed" : "Beds available in this room"}
                              className={`text-[10px] px-1.5 py-0.5 border rounded font-semibold whitespace-nowrap ${cls}`}>
                              {takenOthers + (a.bed_no ? 1 : 0)}/{cap}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- Edit Accommodation Modal ---------- */}
      {editingAcc && (
        <EditAccommodationModal
          acc={editingAcc}
          propTypes={propTypes}
          onClose={() => setEditingAcc(null)}
          onSaved={async () => { setEditingAcc(null); await load(); }}
        />
      )}

      {/* ---------- Occupancy by destination → house → room → pax ---------- */}
      <div className="aii-card p-6">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Users size={16} className="text-brand"/> Occupancy by destination</h3>
        {Object.keys(occupancyByDest).length === 0 ? (
          <div className="text-sm text-[color:var(--aii-muted)] italic text-center py-6">No occupied rooms yet.</div>
        ) : (
          <div className="space-y-4">
            {Object.entries(occupancyByDest).sort().map(([label, data]) => (
              <div key={label} className="border border-[color:var(--aii-border)] rounded-md p-3" data-testid={`occ-dest-${data.country}-${data.city}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><MapPin size={11}/> {label}</div>
                  <span className="pill pill-green">{data.occupants} occupant{data.occupants !== 1 && "s"}</span>
                </div>
                <div className="space-y-3">
                  {Object.values(data.houses)
                    .sort((a, b) => (a.accommodation.name || "").localeCompare(b.accommodation.name || ""))
                    .map(h => (
                      <div key={h.accommodation.id || h.accommodation.name}
                           data-testid={`occ-house-${h.accommodation.id || h.accommodation.name}`}
                           className="rounded-md border border-[color:var(--aii-border)] bg-white overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-[color:var(--aii-green-tint)]">
                          <div className="flex items-center gap-2 min-w-0">
                            <Building2 size={13} className="text-brand shrink-0"/>
                            <div className="min-w-0">
                              <div className="font-semibold text-sm truncate">{h.accommodation.name}</div>
                              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">{h.accommodation.property_type}</div>
                            </div>
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-white border border-[color:var(--aii-border)] font-semibold text-[color:var(--aii-text-2)] whitespace-nowrap">
                            {h.count} pax
                          </span>
                        </div>
                        <div className="divide-y divide-[color:var(--aii-border)]">
                          {Object.values(h.rooms)
                            .sort((a, b) => String(a.room).localeCompare(String(b.room), undefined, { numeric: true }))
                            .map(r => (
                              <div key={r.room} data-testid={`occ-room-${h.accommodation.id}-${r.room}`}
                                   className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2 px-3 py-2.5">
                                <div className="flex items-start gap-1.5 pt-0.5">
                                  <BedDouble size={12} className="text-[color:var(--aii-muted)] mt-0.5"/>
                                  <div>
                                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Room</div>
                                    <div className="text-sm font-semibold">{r.room}</div>
                                    <div className="text-[10px] text-[color:var(--aii-muted)]">{r.pax.length} pax</div>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {r.pax
                                    .sort((a, b) => (a.bed_no || 0) - (b.bed_no || 0))
                                    .map(p => (
                                      <span key={p.booking.id} data-testid={`occ-pax-${p.booking.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded text-xs">
                                        <span className="font-semibold text-[color:var(--aii-text-1)] truncate max-w-[160px]">{p.booking.customer}</span>
                                        <span className="text-[10px] text-[color:var(--aii-muted)]">· Bed {p.bed_no}</span>
                                      </span>
                                    ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <DeleteConfirmDialog
        open={!!delRoomTarget}
        title="Delete this room?"
        description="Existing allocations remain but the room will no longer appear under its property."
        label={delRoomTarget?.name}
        onCancel={() => setDelRoomTarget(null)}
        onConfirm={doDelRoom}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// Edit Accommodation modal — PCWM/GEHM/BDOM/Founder can rename an
// existing property, tweak its address, notes, type or city without
// having to delete + re-create it. Photos & room children stay intact.
// ------------------------------------------------------------------
function EditAccommodationModal({ acc, propTypes, onClose, onSaved }) {
  const [f, setF] = useState({
    country: acc.country || "",
    city: acc.city || "",
    property_type: acc.property_type || "",
    name: acc.name || "",
    address: acc.address || "",
    notes: acc.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.country.trim() || !f.city.trim() || !f.property_type || !f.name.trim()) {
      toast.error("Country, City, Type and Name are required"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/accommodations/${acc.id}`, {
        country: f.country.trim(), city: f.city.trim(), property_type: f.property_type,
        name: f.name.trim(), address: f.address, notes: f.notes,
        photos: acc.photos || [],
      });
      toast.success("Accommodation updated");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid="acc-edit-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Edit accommodation</div>
            <h3 className="font-display text-lg font-semibold">{acc.name}</h3>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><XIcon size={16}/></button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-xs">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Country</span>
            <input className="input-aii w-full mt-1" value={f.country} onChange={e => setF({ ...f, country: e.target.value })} data-testid="acc-edit-country"/>
          </label>
          <label className="block text-xs">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">City</span>
            <input className="input-aii w-full mt-1" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid="acc-edit-city"/>
          </label>
          <label className="block text-xs">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Property type</span>
            <select className="input-aii w-full mt-1" value={f.property_type} onChange={e => setF({ ...f, property_type: e.target.value })} data-testid="acc-edit-type">
              <option value="">— Type —</option>
              {propTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block text-xs">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Property name</span>
            <input className="input-aii w-full mt-1" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="acc-edit-name"/>
          </label>
          <label className="block text-xs sm:col-span-2">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Full address</span>
            <input className="input-aii w-full mt-1" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} data-testid="acc-edit-address"/>
          </label>
          <label className="block text-xs sm:col-span-2">
            <span className="uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Notes</span>
            <textarea rows={2} className="input-aii w-full mt-1" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="acc-edit-notes"/>
          </label>
        </div>
        <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button data-testid="acc-edit-save" onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Inline SVG sparkline — next-30-day bed occupancy per accommodation.
// Green fill under the curve; peak marker for the busiest day.
// ------------------------------------------------------------------
function OccupancySparkline({ data, testId }) {
  if (!data || !data.total_beds || !Array.isArray(data.series) || data.series.length === 0) {
    return (
      <div className="mt-2 text-[10px] text-[color:var(--aii-muted)] italic" data-testid={testId ? `${testId}-empty` : undefined}>
        No occupancy in the next 30 days.
      </div>
    );
  }
  const W = 140, H = 30, PAD = 2;
  const max = Math.max(data.total_beds || 1, ...data.series.map(s => s.used || 0)) || 1;
  const step = data.series.length > 1 ? (W - PAD * 2) / (data.series.length - 1) : 0;
  const points = data.series.map((s, i) => {
    const x = PAD + i * step;
    const y = H - PAD - ((s.used || 0) / max) * (H - PAD * 2);
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${points[points.length - 1][0].toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  const peakIdx = data.series.reduce((best, s, i) => (s.used > data.series[best].used ? i : best), 0);
  const peak = data.series[peakIdx];
  const [pxX, pxY] = points[peakIdx];
  const totalUsedToday = data.series[0]?.used || 0;
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="flex items-center justify-between text-[10px] text-[color:var(--aii-muted)] mb-0.5">
        <span>Next 30d occupancy</span>
        <span>
          <b className="text-[color:var(--aii-text-1)]">{totalUsedToday}</b>/{data.total_beds} today
          {peak.used > 0 && <span className="ml-1.5">· peak {peak.used}</span>}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" className="block">
        <path d={area} fill="var(--aii-green-tint, #d1fae5)" opacity="0.55"/>
        <path d={path} fill="none" stroke="var(--aii-brand, #059669)" strokeWidth="1.5" strokeLinejoin="round"/>
        {peak.used > 0 && (
          <circle cx={pxX} cy={pxY} r="1.8" fill="var(--aii-brand, #059669)"/>
        )}
      </svg>
    </div>
  );
}


// ------------------------------------------------------------------
// Photo gallery per accommodation — GEHM/Founder can attach a few
// preview shots, participants see the same thumbnails on their portal.
// ------------------------------------------------------------------
function PhotoGallery({ acc, canEdit, onUpload, onRemove, inputRef }) {
  const photos = acc.photos || [];
  const token = getToken();
  if (!canEdit && photos.length === 0) return null;
  return (
    <div className="mt-2" data-testid={`acc-photos-${acc.id}`}>
      <div className="flex items-center justify-between text-[10px] text-[color:var(--aii-muted)] mb-1">
        <span className="flex items-center gap-1"><Camera size={10}/> Photos ({photos.length})</span>
        {canEdit && (
          <>
            <input ref={inputRef} type="file" accept="image/*" className="hidden"
                   onChange={(e) => onUpload(e.target.files?.[0])} data-testid={`acc-photo-input-${acc.id}`}/>
            <button type="button"
                    onClick={() => document.querySelector(`[data-testid="acc-photo-input-${acc.id}"]`)?.click()}
                    data-testid={`acc-photo-add-${acc.id}`}
                    className="text-[10px] text-brand font-semibold hover:underline">+ Add</button>
          </>
        )}
      </div>
      {photos.length === 0 ? (
        <div className="text-[10px] text-[color:var(--aii-muted)] italic">No photos yet — add a few so participants see the property.</div>
      ) : (
        <div className="flex gap-1.5 flex-wrap">
          {photos.map(fid => (
            <div key={fid} className="relative group">
              <img src={`${API}/files/${fid}?auth=${token}`} alt="acc"
                   className="w-14 h-14 object-cover rounded border border-[color:var(--aii-border)]"
                   data-testid={`acc-photo-${acc.id}-${fid}`}/>
              {canEdit && (
                <button type="button" onClick={() => onRemove(fid)}
                        title="Remove"
                        data-testid={`acc-photo-del-${acc.id}-${fid}`}
                        className="absolute -top-1 -right-1 bg-white border border-red-300 text-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                  <XIcon size={10}/>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
